import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";
import {
  listMyRecommendations,
  approveRecommendation,
  dismissRecommendation,
} from "../../api/recommendationsApi";
import FeatureGate from "../../components/gating/FeatureGate";

// Pending agent recommendations the user can approve / dismiss. Conflict
// pairs (Compass says BUY · Vigil says SELL on same symbol within 10 min)
// are rendered side-by-side so the user picks one — approving auto-
// dismisses the partner server-side.

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

// Agent → colour chip. Mirrors the ChatWidget palette so the user reads
// "Compass said this" with the same visual cue across surfaces.
const AGENT_STYLES = {
  compass: "bg-indigo-500/15 text-indigo-500",
  lens:    "bg-sky-500/15 text-sky-500",
  vigil:   "bg-rose-500/15 text-rose-500",
  tally:   "bg-amber-500/15 text-amber-500",
  terra:   "bg-emerald-500/15 text-emerald-500",
  anchor:  "bg-violet-500/15 text-violet-500",
};

const ACTION_STYLES = {
  buy:       "bg-emerald-500/15 text-emerald-500",
  sell:      "bg-rose-500/15 text-rose-500",
  hold:      "bg-gray-500/15 text-gray-500",
  rebalance: "bg-indigo-500/15 text-indigo-500",
  alert:     "bg-amber-500/15 text-amber-500",
};

const RecommendationCard = ({ rec, onDecide, busy }) => {
  const agentCls = AGENT_STYLES[rec.agent_name] || "bg-gray-500/15 text-gray-500";
  const actionCls = ACTION_STYLES[rec.action_type] || "bg-gray-500/15 text-gray-500";
  return (
    <Card>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {rec.agent_name && (
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${agentCls}`}>
                {rec.agent_name}
              </span>
            )}
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${actionCls}`}>
              {rec.action_type}
            </span>
            {rec.advisor_display_name && (
              <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-500">
                via {rec.advisor_display_name}
              </span>
            )}
            {rec.skill_name && (
              <span className="text-[10px] text-gray-500">· skill: {rec.skill_name}</span>
            )}
          </div>
          <p className="text-lg font-bold">
            {rec.action_type.toUpperCase()}
            {rec.symbol && (
              <>
                {" "}
                <span className="font-mono">{rec.symbol}</span>
              </>
            )}
            {rec.target_quantity && (
              <span className="text-base font-normal text-gray-500"> · {rec.target_quantity}</span>
            )}
          </p>
          {rec.rationale && (
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 leading-relaxed">
              {rec.rationale}
            </p>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecide(rec.id, "approve")}
            className="px-3 py-2 rounded-lg bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/25 text-sm font-semibold flex items-center gap-1 disabled:opacity-50"
          >
            <CheckIcon className="w-4 h-4" />
            Approve
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecide(rec.id, "dismiss")}
            className="px-3 py-2 rounded-lg text-gray-500 hover:bg-gray-500/10 text-sm font-medium flex items-center gap-1 disabled:opacity-50"
          >
            <XMarkIcon className="w-4 h-4" />
            Dismiss
          </button>
        </div>
      </div>
    </Card>
  );
};

const ConflictPair = ({ recs, onDecide, busy }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2 text-amber-500 px-1">
      <ExclamationTriangleIcon className="w-4 h-4" />
      <span className="text-xs font-bold uppercase tracking-widest">
        Conflicting recommendations — pick one
      </span>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {recs.map((rec) => (
        <RecommendationCard key={rec.id} rec={rec} onDecide={onDecide} busy={busy === rec.id} />
      ))}
    </div>
  </div>
);

const Recommendations = () => {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      setRecs(await listMyRecommendations({ status: "pending" }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Group conflict-paired recommendations together so they render side-by-side.
  // Standalones come through as single-element groups.
  const grouped = useMemo(() => {
    const byId = new Map(recs.map((r) => [r.id, r]));
    const seen = new Set();
    const groups = [];
    for (const r of recs) {
      if (seen.has(r.id)) continue;
      if (r.conflicts_with && byId.has(r.conflicts_with) && !seen.has(r.conflicts_with)) {
        groups.push([r, byId.get(r.conflicts_with)]);
        seen.add(r.id);
        seen.add(r.conflicts_with);
      } else {
        groups.push([r]);
        seen.add(r.id);
      }
    }
    return groups;
  }, [recs]);

  const onDecide = async (id, decision) => {
    setBusyId(id);
    try {
      if (decision === "approve") await approveRecommendation(id);
      else await dismissRecommendation(id);
      load();
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <Card>Loading recommendations…</Card>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Recommendations</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
          Pending actions from your AI specialists, shaped by your active advisor
          subscriptions. Approve to mark "I'll do this" — FinAI does not place
          trades, you act on it in your brokerage.
        </p>
      </div>

      {recs.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No pending recommendations. Recommendations appear here when you chat
            with the Council and an agent commits a concrete action — usually
            shaped by an advisor strategy you've subscribed to.{" "}
            <Link to="/marketplace" className="text-indigo-500 hover:underline">
              Browse the marketplace →
            </Link>
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {grouped.map((group) =>
            group.length === 2 ? (
              <ConflictPair
                key={`pair-${group[0].id}-${group[1].id}`}
                recs={group}
                onDecide={onDecide}
                busy={busyId}
              />
            ) : (
              <RecommendationCard
                key={group[0].id}
                rec={group[0]}
                onDecide={onDecide}
                busy={busyId === group[0].id}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
};

const RecommendationsGated = () => (
  <FeatureGate feature="recommendations">
    <Recommendations />
  </FeatureGate>
);

export default RecommendationsGated;
